import React from "react";
import { motion } from "framer-motion";
import { Upload, Cpu, FileDown } from "lucide-react";

const steps = [
  {
    icon: Upload,
    number: "01",
    title: "Upload Post-Op Image",
    description:
      "Submit a high-resolution photograph of the recipient scalp area taken after the hair transplant procedure. The system supports standard image formats and accepts both photo and video input.",
  },
  {
    icon: Cpu,
    number: "02",
    title: "AI Analysis",
    description:
      "A deep learning model processes the image, detecting individual graft units and computing their spatial distribution. Density is calculated and mapped across scalp zones.",
  },
  {
    icon: FileDown,
    number: "03",
    title: "Receive Your Report",
    description:
      "Download a structured PDF report containing the estimated graft count, density distribution map, high/low density zone breakdown, and full analysis metadata.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      className="relative py-24 sm:py-32 overflow-hidden bg-[#060C18]"
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(21,69,202,0.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(21,69,202,0.04) 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-16 sm:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-royal-500/10 border border-royal-500/20 text-royal-300 text-xs font-medium tracking-[0.1em] uppercase mb-6">
            Process
          </div>
          <h2
            className="text-white font-semibold tracking-tight leading-tight mb-4"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}
          >
            How Graft Counter works
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed">
            A straightforward three-step process — from image upload to a
            verified, downloadable report.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.14 }}
              className="relative bg-white/5 border border-white/10 rounded-2xl p-7 hover:border-royal-500/25 hover:bg-white/10 transition-all duration-300 group"
            >
              {/* Step number connector (desktop only) */}
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-14 -right-3 w-6 h-px bg-white/10 z-10" />
              )}

              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 bg-royal-500/10 border border-royal-500/20 rounded-xl flex items-center justify-center group-hover:bg-royal-500/15 transition-colors duration-300">
                  <step.icon className="w-5 h-5 text-royal-400 stroke-[1.5]" />
                </div>
                <span className="text-3xl font-light text-white/12 tracking-wider">
                  {step.number}
                </span>
              </div>

              <h3 className="text-white font-semibold text-lg mb-3 leading-snug">
                {step.title}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
